<template>
    <v-container>
        <v-form ref="vatRecordForm" v-model="valid" lazy-validation>
            <v-row justify="space-around">
                <v-col cols="12" md="4">
                    <span style="font-weight:bold" v-if="!draft">{{ $t('individualVatRecordDetails') }}</span>
                    <span style="font-weight:bold" v-else>{{ $t('createIndividualVatRecord') }}</span>
                </v-col>
                <v-col cols="12" md="4" align="right">
                    <v-tooltip right color="#2b5d66">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon v-show="vatRecord.versions != null && vatRecord.versions.length > 1" v-bind="attrs"
                                v-on="on" @click="showVersion = !showVersion">
                                history
                            </v-icon>
                        </template>
                        <span>{{ $t('vatRecordVersions') }}</span>
                    </v-tooltip>
                </v-col>
            </v-row>
            <v-row justify="space-around">
                <v-col cols="8" md="4">
                    <v-textarea auto-grow rows="1" :disabled="!draft" v-bind:label="$t('calculationNumber') + '*'"
                        v-model.trim="vatRecord.calculationNumber" :rules="documentNumberRule()"
                        @input="checkDocumentNumber()">
                    </v-textarea>
                    <br />
                    <span style="font-weight:bold;">{{ $t('status') }}: {{ documentStatusText() }}</span><br /><br />
                    <v-menu v-model="menuTurnoverDate" :close-on-content-click="false" transition="scale-transition"
                        offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field disabled v-model="vatRecord.turnoverDate" v-bind:label="$t('turnoverDate')"
                                prepend-inner-icon="mdi-calendar" readonly clearable @click:clear="clearTurnoverDate"
                                v-bind="attrs" v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="vatRecord.turnoverDate" no-title scrollable min="2017-01-01"
                            max="2050-01-01" @input="menuTurnoverDate = false">
                        </v-date-picker>
                    </v-menu>
                    <v-text-field v-model="vatRecord.year" v-bind:label="$t('year') + '*'"
                        :disabled="!(draft || correction)" :rules="[v => !!v || $t('yearRequired')]" clearable>
                    </v-text-field>
                    <v-select v-model="vatRecord.documentDirection" :items="documentDirectionsItems"
                        :item-text="getDocumentDirectionText" item-value="documentDirectionId"
                        :disabled="!(draft || correction)" :rules="[v => !!v || $t('documentDirectionRequired')]"
                        @change="documentDirectionChanged()" v-bind:label="$t('documentDirection')"
                        v-bind:no-data-text="$t('noData')" return-object required>
                    </v-select>
                    <v-select v-model="vatRecord.periodType.vatPeriodTypeId" :items="vatPeriodTypeItems"
                        :item-text="getVatPeriodTypeText" item-value="vatPeriodTypeId"
                        :rules="[v => !!v || $t('timeRangeRequired')]" :disabled="!(draft || correction)"
                        v-bind:label="$t('timeRange')" v-bind:no-data-text="$t('noData')" @change="periodTypeChanged"
                        required>
                    </v-select>
                    <v-select v-model="vatRecord.period" :items="vatPeriodItems" :item-text="getVatPeriodText"
                        item-value="vatPeriodId" :rules="[v => !!v || $t('periodRequired')]"
                        :disabled="!(draft || correction)" v-bind:label="$t('period')"
                        v-bind:no-data-text="$t('noData')" return-object required>
                    </v-select>
                    <v-text-field v-model="vatRecord.relatedPartyIdentifier" v-bind:label="getRelatedPartyIdLabel"
                        :rules="[v => !!v || $t('required')]" :disabled="!(draft || correction)" clearable>
                    </v-text-field>
                    <!--<v-checkbox
                v-model="vatRecord.foreignDocument"
                v-bind:label="$t('foreignDocument')"
                :disabled="!(draft || correction)"
                color="#2b5d66">
                </v-checkbox>-->
                    <v-select v-model="vatRecord.documentType" :items="documentTypesItems"
                        :item-text="getDocumentTypeText" item-value="vatRecordDocumentTypeId"
                        :rules="[v => !!v || $t('documentTypeRequired')]" :disabled="!(draft || correction)"
                        @change="documentTypeChanged()" v-bind:label="$t('documentType')"
                        v-bind:no-data-text="$t('noData')" return-object required>
                    </v-select>
                    <v-text-field v-if="vatRecord.documentType.vatRecordDocumentTypeId < 5"
                        v-model="vatRecord.documentNumber" v-bind:label="$t('relatedDocumentNumber') + '*'"
                        :disabled="!(draft || correction)" :rules="relatedDocumentNumberRule" clearable>
                    </v-text-field>
                    <v-text-field v-if="vatRecord.documentType.vatRecordDocumentTypeId >= 5"
                        v-model="vatRecord.internalInvoiceNumber" v-bind:label="$t('internalInvoiceNumber') + '*'"
                        :disabled="!(draft || correction)" :rules="internalInvoiceNumberRule" clearable>
                    </v-text-field>
                    <v-select v-if="vatRecord.documentType.vatRecordDocumentTypeId < 4"
                        v-model="vatRecord.relatedInvoiceOption" :items="relatedInvoiceOptionsItems"
                        :item-text="getRelatedInvoiceOptionText" item-value="relatedInvoiceOptionId"
                        :disabled="!(draft || correction)" @change="relatedInvoiceOptionChanged()"
                        v-bind:label="$t('relatedInvoiceOption')" v-bind:no-data-text="$t('noData')" return-object>
                    </v-select>
                    <v-select v-if="vatRecord.documentType && vatRecord.documentType.vatRecordDocumentTypeId > 4"
                        v-model="vatRecord.internalInvoiceOption" :items="internalInvoiceOptionItems"
                        :item-text="getOptionText" item-value="internalInvoiceOptionId"
                        :rules="[v => !!v || $t('internalInvoiceOptionRequired')]" :disabled="!(draft || correction)"
                        v-bind:label="$t('internalInvoiceOption')" v-bind:no-data-text="$t('noData')"
                        @change="internalInvoiceOptionChanged()" return-object required>
                    </v-select>
                    <v-select
                        v-if="vatRecord.documentType.vatRecordDocumentTypeId >= 5 && vatRecord.internalInvoiceOption.internalInvoiceOptionId != 2"
                        v-model="vatRecord.relatedInternalInvoiceOption" :items="relatedInternalInvoiceOptionsItems"
                        :item-text="getRelatedInternalInvoiceOptionText" item-value="relatedInternalInvoiceOptionId"
                        :disabled="!(draft || correction)" @change="relatedInternalInvoiceOptionChanged()"
                        v-bind:label="$t('relatedInternalInvoiceOption')" v-bind:no-data-text="$t('noData')"
                        return-object>
                    </v-select>
                    <v-text-field
                        v-if="((vatRecord.documentType.vatRecordDocumentTypeId < 5 && (vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 1 || vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 3)) || (vatRecord.documentType.vatRecordDocumentTypeId >= 5 && vatRecord.relatedInternalInvoiceOption.relatedInternalInvoiceOptionId == 1))"
                        v-model="relatedDocument" v-bind:label="getRelatedInvoiceOptionLabel"
                        :disabled="!(draft || correction)" clearable>
                        <v-tooltip right slot="append-outer" color="#2b5d66">
                            <template #activator="{ on }">
                                <!-- Original -->
                                <v-icon v-on="on" :disabled="!(draft || correction)" @click="addRelatedDocument"
                                    style="cursor:pointer;color:#008B8B;">control_point</v-icon>
                                <!-- UC
                                <v-icon v-on="on" 
                                :disabled="!(draft || correction)"
                                @click="addRelatedDocument"
                                style="cursor:pointer;color:#2b5d66;"
                                >control_point</v-icon> -->
                            </template>
                            <span>{{ $t('addDocument') }}</span>
                        </v-tooltip>
                    </v-text-field>
                    <v-chip-group column
                        v-if="(vatRecord.documentType.vatRecordDocumentTypeId < 5 && (vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 1 || vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 3) || (vatRecord.documentType.vatRecordDocumentTypeId >= 5 && vatRecord.relatedInternalInvoiceOption.relatedInternalInvoiceOptionId != 2))">
                        <v-chip v-for="(relatedDocument, index) in vatRecord.relatedDocuments" :key="index"
                            :close="vatRecord.status.vatRecordStatusId == 1" outlined :disabled="!(draft || correction)"
                            @click:close="removeFromRelatedDocument(index)">
                            {{ relatedDocument.documentNumber }}
                        </v-chip>
                    </v-chip-group>
                    <v-menu v-if="vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 2" v-model="menuStart"
                        :disabled="!(draft || correction)" :close-on-content-click="false" transition="scale-transition"
                        offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-if="vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 2"
                                v-model.trim="vatRecord.relatedInvoiceIssueDateFrom" v-bind:label="$t('periodOf') + '*'"
                                :disabled="!(draft || correction)" prepend-inner-icon="mdi-calendar" readonly clearable
                                v-bind="attrs" v-on="on" :rules="invoicePeriodStartRule()"></v-text-field>
                        </template>
                        <v-date-picker v-model="vatRecord.relatedInvoiceIssueDateFrom" no-title scrollable
                            min="2017-01-01" max="2050-01-01" @input="menuStart = false">
                        </v-date-picker>
                    </v-menu>
                    <v-menu v-if="vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 2" v-model="menuEnd"
                        :disabled="!(draft || correction)" :close-on-content-click="false" transition="scale-transition"
                        offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-textarea v-if="vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 2" auto-grow
                                rows="1" v-model.trim="vatRecord.relatedInvoiceIssueDateTo"
                                v-bind:label="$t('periodTo') + '*'" :disabled="!(draft || correction)"
                                prepend-inner-icon="mdi-calendar" readonly clearable v-bind="attrs" v-on="on"
                                :rules="invoicePeriodEndRule()"></v-textarea>
                        </template>
                        <v-date-picker v-model="vatRecord.relatedInvoiceIssueDateTo" no-title scrollable
                            min="2017-01-01" max="2050-01-01" @input="menuEnd = false">
                        </v-date-picker>
                    </v-menu>
                    <v-text-field
                        v-if="vatRecord.documentType.vatRecordDocumentTypeId == 4 || (vatRecord.internalInvoiceOption && vatRecord.internalInvoiceOption.internalInvoiceOptionId == 2)"
                        :disabled="!(draft || correction)" v-model="vatRecord.basisForPrepayment"
                        v-bind:label="$t('basisForPrepayment')" clearable>
                    </v-text-field>
                    <v-menu v-model="menuPaymentDate"
                        v-if="vatRecord.documentType.vatRecordDocumentTypeId == 4 || (vatRecord.internalInvoiceOption && vatRecord.internalInvoiceOption.internalInvoiceOptionId == 2)"
                        :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-if="vatRecord.documentType.vatRecordDocumentTypeId == 4 || (vatRecord.internalInvoiceOption && vatRecord.internalInvoiceOption.internalInvoiceOptionId == 2)"
                                :disabled="!(draft || correction)" v-model="vatRecord.paymentDate"
                                v-bind:label="$t('paymentprepaymentDate')" prepend-inner-icon="mdi-calendar" readonly
                                clearable @click:clear="clearPaymentDate" v-bind="attrs" v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="vatRecord.paymentDate" no-title scrollable min="2017-01-01"
                            max="2050-01-01" @input="menuPaymentDate = false">
                        </v-date-picker>
                    </v-menu>
                </v-col>
                <v-col :cols="dynamicCol" :md="mdDynamic">
                    <!--<v-text-field
                :disabled="!(draft || correction)"
                v-model="vatRecord.turnoverAmount"
                v-bind:label="$t('turnoverAmount')"
                :rules="[v => (/^(?!,$)[\d,.]+$/.test(v) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                @change="format()"
                clearable>
                </v-text-field>-->
                    <!-- NoRealEstate -->
                    <span style="font-size:16px;font-weight:bold;">{{ getNoRealEstateText20 }}</span><br /><br />
                    <v-textarea auto-grow rows="1" :disabled="!(draft || correction)"
                        v-model="vatRecord.noRealEstateDescription20" v-bind:label="$t('description')" clearable>
                    </v-textarea>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.noRealEstateBaseAmount20"
                        v-bind:label="$t('baseAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.noRealEstateCalculatedVat20"
                        v-bind:label="$t('vatAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <br />
                    <span style="font-size:16px;font-weight:bold;">{{ getNoRealEstateText10 }}</span><br /><br />
                    <v-textarea auto-grow rows="1" :disabled="!(draft || correction)"
                        v-model="vatRecord.noRealEstateDescription10" v-bind:label="$t('description')" clearable>
                    </v-textarea>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.noRealEstateBaseAmount10"
                        v-bind:label="$t('baseAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.noRealEstateCalculatedVat10"
                        v-bind:label="$t('vatAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <br /><br /><br /><br />
                    <span style="font-size:16px;font-weight: bold;">{{ $t('totalVatAmount') }}</span> <br />
                    <span style="font-size:16px;font-weight: bold;">{{ vatRecord.vatAmount }}</span><br />
                    <br />
                    <span style="font-size:16px;font-weight: bold;">{{ $t('vatRecordTotalAmount') }}</span> <br />
                    <span style="font-size:16px;font-weight: bold;">{{ vatRecord.totalAmount }}</span>
                </v-col>
                <!-- RealEstate -->
                <v-col :cols="dynamicCol" :md="mdDynamic"
                    v-if="vatRecord.internalInvoiceOption.internalInvoiceOptionId >= 1">
                    <span style="font-size:16px;font-weight:bold;">{{ getRealEstateText20 }}</span><br /><br />
                    <v-textarea auto-grow rows="1" :disabled="!(draft || correction)"
                        v-model="vatRecord.realEstateDescription20" v-bind:label="$t('description')" clearable>
                    </v-textarea>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.realEstateBaseAmount20"
                        v-bind:label="$t('baseAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.realEstateCalculatedVat20"
                        v-bind:label="$t('vatAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <br />
                    <span style="font-size:16px;font-weight:bold;">{{ getRealEstateText10 }}</span><br /><br />
                    <v-textarea auto-grow rows="1" :disabled="!(draft || correction)"
                        v-model="vatRecord.realEstateDescription10" v-bind:label="$t('description')" clearable>
                    </v-textarea>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.realEstateBaseAmount10"
                        v-bind:label="$t('baseAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.realEstateCalculatedVat10"
                        v-bind:label="$t('vatAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                </v-col>
                <!-- NoChargeNoRealEstate -->
                <v-col :cols="dynamicCol" :md="mdDynamic"
                    v-if="vatRecord.internalInvoiceOption.internalInvoiceOptionId == 1">
                    <span style="font-size:16px;font-weight:bold;">{{ $t('turnoverAE20WithoutFee') }}</span><br /><br />
                    <v-textarea auto-grow rows="1" :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeNoRealEstateDescription20" v-bind:label="$t('description')"
                        clearable>
                    </v-textarea>
                    <v-text-field :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeNoRealEstateBaseAmount20" v-bind:label="$t('baseAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <v-text-field :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeNoRealEstateCalculatedVat20" v-bind:label="$t('vatAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <br />
                    <span style="font-size:16px;font-weight:bold;">{{ $t('turnoverAE10WithoutFee') }}</span><br /><br />
                    <v-textarea auto-grow rows="1" :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeNoRealEstateDescription10" v-bind:label="$t('description')"
                        clearable>
                    </v-textarea>
                    <v-text-field :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeNoRealEstateBaseAmount10" v-bind:label="$t('baseAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <v-text-field :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeNoRealEstateCalculatedVat10" v-bind:label="$t('vatAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                </v-col>
                <!-- NoChargeRealEstate -->
                <v-col :cols="dynamicCol" :md="mdDynamic"
                    v-if="vatRecord.internalInvoiceOption.internalInvoiceOptionId == 1">
                    <span style="font-size:16px;font-weight:bold;">{{ $t('turnoverAE20WithoutFeeObj')
                        }}</span><br /><br />
                    <v-textarea auto-grow rows="1" :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeRealEstateDescription20" v-bind:label="$t('description')" clearable>
                    </v-textarea>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.noChargeRealEstateBaseAmount20"
                        v-bind:label="$t('baseAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <v-text-field :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeRealEstateCalculatedVat20" v-bind:label="$t('vatAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <br />
                    <span style="font-size:16px;font-weight:bold;">{{ $t('turnoverAE10WithoutFeeObj')
                        }}</span><br /><br />
                    <v-textarea auto-grow rows="1" :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeRealEstateDescription10" v-bind:label="$t('description')" clearable>
                    </v-textarea>
                    <v-text-field :disabled="!(draft || correction)" v-model="vatRecord.noChargeRealEstateBaseAmount10"
                        v-bind:label="$t('baseAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                    <v-text-field :disabled="!(draft || correction)"
                        v-model="vatRecord.noChargeRealEstateCalculatedVat10" v-bind:label="$t('vatAmount')"
                        :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                        @change="format()" @input="calculateTotal()" clearable>
                    </v-text-field>
                </v-col>
            </v-row>
            <br /><br />
            <v-row class="footer" no-gutters>
                <v-btn class="mx-2" color="#2b5d66" :dark="hasChanged && !executingOperation"
                    :disabled="!hasChanged || executingOperation" v-if="draft" @click="save">
                    {{ $t('save') }}
                </v-btn>
                <v-btn class="mx-2" color="#2b5d66" :dark="hasChanged && !executingOperation"
                    :disabled="!hasChanged || executingOperation" v-if="draft" @click="cancelChanges">
                    {{ $t('discardChanges') }}
                </v-btn>
                <v-btn class="mx-2" color="#2b5d66" v-if="draft" :dark="!executingOperation"
                    :disabled="executingOperation" @click="send">
                    {{ $t('send') }}
                </v-btn>
                <v-btn class="mx-2" color="#2b5d66" :dark="!executingOperation" :disabled="executingOperation"
                    v-if="draft" @click="deleteRecord">
                    {{ $t('delete') }}
                </v-btn>
                <v-btn :dark="!executingOperation" :disabled="executingOperation" class="mx-2" color="#2b5d66"
                    v-if="recorded && !correction" @click="correct">
                    {{ $t('correct') }}
                </v-btn>
                <v-btn :dark="!executingOperation" :disabled="executingOperation" class="mx-2" color="#2b5d66"
                    v-if="correction" @click="saveCorrection">
                    {{ $t('saveCorrection') }}
                </v-btn>
                <v-btn :dark="!executingOperation" :disabled="executingOperation" class="mx-2" color="#2b5d66"
                    v-if="correction" @click="cancelCorrection">
                    {{ $t('cancelCorrection') }}
                </v-btn>
                <v-btn :dark="!executingOperation" :disabled="executingOperation" v-if="recorded" class="mx-2"
                    color="#2b5d66" @click="annul">
                    {{ $t('annul') }}
                </v-btn>
            </v-row>
        </v-form>
        <VersionDialog :versions="vatRecord.versions" v-if="showVersion" />
    </v-container>
</template>
<script>
import moment from "moment"
import { i18n } from '../main'
import VersionDialog from "../components/VatRecordVersionsDialog.vue"
export default {
    name: "IndividualVatForm",
    components: {
        VersionDialog
    },
    props: ['id'],
    data() {
        return {
            valid: true,
            correction: false,
            showVersion: false,
            foundNumber: false,
            dynamicCol: 8,
            mdDynamic: 4,
            initialVatRecord: {
                individualVatRecordId: -1,
                individualVatId: -1,
                calculationNumber: "",
                year: "",
                documentType: {
                    vatRecordDocumentTypeId: 1
                },
                documentDirection: {
                    documentDirectionId: 1
                },
                periodType: {
                    vatPeriodTypeId: 1
                },
                period: {
                    vatPeriodId: 1
                },
                status: {
                    vatRecordStatusId: 1,
                    vatRecordStatus: 'U pripremi',
                    vatRecordStatusEng: 'Draft'
                },
                internalInvoiceOption: {
                    internalInvoiceOptionId: -1,
                    optionSrb: 'Promet',
                    optionEng: 'Turnover'
                },
                documentNumber: null,
                internalInvoiceNumber: null,
                relatedPartyIdentifier: "",
                foreignDocument: false,
                relatedInvoiceOption: {
                    relatedInvoiceOptionId: 0
                },
                relatedInternalInvoiceOption: {
                    relatedInternalInvoiceOptionId: 0
                },
                relatedDocuments: [],
                basisForPrepayment: "",
                paymentDate: "",
                turnoverDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                    .toISOString()
                    .substr(0, 10),
                noRealEstateDescription20: "",
                noRealEstateDescription10: "",
                turnoverAmount: this.formatNumberToString(0),
                noRealEstateBaseAmount20: null,
                noRealEstateBaseAmount10: null,
                noRealEstateCalculatedVat20: null,
                noRealEstateCalculatedVat10: null,
                realEstateDescription20: "",
                realEstateDescription10: "",
                realEstateBaseAmount20: null,
                realEstateBaseAmount10: null,
                realEstateCalculatedVat20: null,
                realEstateCalculatedVat10: null,
                noChargeNoRealEstateDescription20: "",
                noChargeNoRealEstateDescription10: "",
                noChargeNoRealEstateBaseAmount20: null,
                noChargeNoRealEstateBaseAmount10: null,
                noChargeNoRealEstateCalculatedVat20: null,
                noChargeNoRealEstateCalculatedVat10: null,
                noChargeRealEstateDescription20: "",
                noChargeRealEstateDescription10: "",
                noChargeRealEstateBaseAmount20: null,
                noChargeRealEstateBaseAmount10: null,
                noChargeRealEstateCalculatedVat20: null,
                noChargeRealEstateCalculatedVat10: null,
                vatAmount: this.formatNumberToString(0),
                totalAmount: this.formatNumberToString(0),
                versions: [],
                relatedInvoiceIssueDateFrom: "",
                relatedInvoiceIssueDateTo: ""
            },
            vatRecord: {
                individualVatRecordId: -1,
                individualVatId: -1,
                calculationNumber: "",
                year: "",
                documentType: {
                    vatRecordDocumentTypeId: 1
                },
                documentDirection: {
                    documentDirectionId: 1
                },
                periodType: {
                    vatPeriodTypeId: 1,
                },
                period: {
                    vatPeriodId: 1
                },
                status: {
                    vatRecordStatusId: 1,
                    vatRecordStatus: 'U pripremi',
                    vatRecordStatusEng: 'Draft'
                },
                internalInvoiceOption: {
                    internalInvoiceOptionId: -1,
                    optionSrb: 'Promet',
                    optionEng: 'Turnover'
                },
                documentNumber: null,
                internalInvoiceNumber: null,
                relatedPartyIdentifier: "",
                foreignDocument: false,
                relatedInvoiceOption: {
                    relatedInvoiceOptionId: 0
                },
                relatedInternalInvoiceOption: {
                    relatedInternalInvoiceOptionId: 0
                },
                relatedDocuments: [],
                basisForPrepayment: "",
                paymentDate: "",
                turnoverDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                    .toISOString()
                    .substr(0, 10),
                noRealEstateDescription20: "",
                noRealEstateDescription10: "",
                turnoverAmount: this.formatNumberToString(0),
                noRealEstateBaseAmount20: null,
                noRealEstateBaseAmount10: null,
                noRealEstateCalculatedVat20: null,
                noRealEstateCalculatedVat10: null,
                realEstateDescription20: "",
                realEstateDescription10: "",
                realEstateBaseAmount20: null,
                realEstateBaseAmount10: null,
                realEstateCalculatedVat20: null,
                realEstateCalculatedVat10: null,
                noChargeNoRealEstateDescription20: "",
                noChargeNoRealEstateDescription10: "",
                noChargeNoRealEstateBaseAmount20: null,
                noChargeNoRealEstateBaseAmount10: null,
                noChargeNoRealEstateCalculatedVat20: null,
                noChargeNoRealEstateCalculatedVat10: null,
                noChargeRealEstateDescription20: "",
                noChargeRealEstateDescription10: "",
                noChargeRealEstateBaseAmount20: null,
                noChargeRealEstateBaseAmount10: null,
                noChargeRealEstateCalculatedVat20: null,
                noChargeRealEstateCalculatedVat10: null,
                vatAmount: this.formatNumberToString(0),
                totalAmount: this.formatNumberToString(0),
                versions: [],
                relatedInvoiceIssueDateFrom: "",
                relatedInvoiceIssueDateTo: ""
            },
            menuPaymentDate: false,
            menuTurnoverDate: false,
            menuStart: false,
            menuEnd: false,
            relatedDocument: "",
            documentTypesItems: [],
            allDocumentTypesItems: [],
            documentDirectionsItems: [],
            vatPeriodTypeItems: [],
            vatPeriodAllItems: [],
            vatPeriodItems: [],
            internalInvoiceOptionItems: [],
            relatedInvoiceOptionsItems: [],
            allRelatedInvoiceOptionsItems: [],
            relatedInternalInvoiceOptionsItems: [],
            allRelatedInternalInvoiceOptionsItems: [],
            executingOperation: false
        }
    },
    async beforeRouteLeave(to, from, next) {
        if (this.hasChanged && to.name != 'Login' && this.vatRecord.status.vatRecordStatusId == 1 && !await this.$confirm(this.$t('hasChanges'), { title: this.$t('warning'), buttonTrueText: this.$t('yes'), buttonFalseText: this.$t('no'), persistent: true })) {
            next(false)
        } else {
            next()
        }
    },
    created() {
        this.init();
        window.addEventListener('beforeunload', this.beforeWindowUnload);
    },
    computed: {
        hasChanged() {
            return JSON.stringify(this.vatRecord) !== JSON.stringify(this.initialVatRecord)
        },
        draft() {
            return this.vatRecord.status.vatRecordStatusId == 1;
        },
        recorded() {
            return this.vatRecord.status.vatRecordStatusId == 2;
        },
        selectedOrganization() {
            return this.$store.getters.organizationId;
        },
        relatedDocumentNumberRule() {
            const rules = []
            //if (this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 5) {
            const rule = v => !!v || this.$t('relatedDocumentNumberRequired')
            rules.push(rule)
            //}
            return rules
        },
        internalInvoiceNumberRule() {
            const rules = []
            if (this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 5) {
                const rule = v => !!v || this.$t('internalDocumentNumberRequired')
                rules.push(rule)
            }
            return rules
        },
        getRelatedPartyIdLabel() {
            if (this.vatRecord.documentDirection.documentDirectionId == 1)
                return this.$t('relatedPartyIdentifierIn') + '*';
            else
                return this.$t('relatedPartyIdentifierOut') + '*';
        },
        getRelatedInvoiceOptionLabel() {
            if (this.vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 1)
                return this.$t('relatedInvoiceNumber');
            if (this.vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 3)
                return this.$t('relatedPrepaymentNumber');
            if (this.vatRecord.documentType.vatRecordDocumentTypeId > 4)
                return this.$t('relatedInternalInvoiceNumber');
            return ""
        },
        getNoRealEstateText20() {
            if ((this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 4) || (this.vatRecord.relatedInvoiceOption && this.vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 3))
                return this.$t('prepayment20');
            else if (this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 5 && this.vatRecord.internalInvoiceOption && this.vatRecord.internalInvoiceOption.internalInvoiceOptionId != 2)
                return this.$t('turnoverAE20');
            else if (this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 5 && this.vatRecord.internalInvoiceOption && this.vatRecord.internalInvoiceOption.internalInvoiceOptionId == 2)
                return this.$t('prepaymentAE20');
            else
                return this.$t('turnover20');
        },
        getNoRealEstateText10() {
            if ((this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 4) || (this.vatRecord.relatedInvoiceOption && this.vatRecord.relatedInvoiceOption.relatedInvoiceOptionId == 3))
                return this.$t('prepayment10');
            else if (this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 5 && this.vatRecord.internalInvoiceOption && this.vatRecord.internalInvoiceOption.internalInvoiceOptionId != 2)
                return this.$t('turnoverAE10');
            else if (this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 5 && this.vatRecord.internalInvoiceOption && this.vatRecord.internalInvoiceOption.internalInvoiceOptionId == 2)
                return this.$t('prepaymentAE10');
            else
                return this.$t('turnover10');
        },
        getRealEstateText20() {
            if (this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 5 && this.vatRecord.internalInvoiceOption && this.vatRecord.internalInvoiceOption.internalInvoiceOptionId != 2)
                return this.$t('turnoverAE20Obj');
            else
                return this.$t('prepaymentAE20Obj');
        },
        getRealEstateText10() {
            if (this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId == 5 && this.vatRecord.internalInvoiceOption && this.vatRecord.internalInvoiceOption.internalInvoiceOptionId != 2)
                return this.$t('turnoverAE10Obj');
            else
                return this.$t('prepaymentAE10Obj');
        }
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.beforeWindowUnload)
    },
    methods: {
        async init() {
            await this.getDocumentTypes();
            await this.getDocumentDirections();
            await this.getVatPeriodType();
            await this.getVatPeriod();
            await this.getInternalInvoiceOption();
            await this.getRelatedInvoiceOptions();
            await this.getRelatedInternalInvoiceOptions();
            await this.vatRecordData();
            if (this.vatRecord.individualVatRecordId <= 0) {
                this.vatRecord.documentDirection = this.documentDirectionsItems[1];
                this.vatRecord.period = this.vatPeriodItems[0];
                this.documentDirectionChanged();
                this.documentTypeChanged();
            }
        },
        async getDocumentTypes() {
            await this.axios.get('InvoiceUtilities/GetVatRecordDocumentTypes').then((response) => {
                this.allDocumentTypesItems = response.data;
            })
                .catch(err => console.log(err));
        },
        async getDocumentDirections() {
            await this.axios.get('InvoiceUtilities/GetDocumentDirections').then((response) => {
                this.documentDirectionsItems = response.data;
            })
                .catch(err => console.log(err));
        },
        async getVatPeriodType() {
            await this.axios.get('InvoiceUtilities/GetVatPeriodType').then((response) => {
                this.vatPeriodTypeItems = response.data;
            })
                .catch(err => console.log(err));
        },
        async getVatPeriod() {
            await this.axios.get('InvoiceUtilities/GetVatPeriod').then((response) => {
                this.vatPeriodAllItems = response.data;
                this.vatPeriodItems = this.vatPeriodAllItems.filter(p => { return p.vatPeriodTypeId === this.vatRecord.periodType.vatPeriodTypeId });

            })
                .catch(err => console.log(err));
        },
        async getInternalInvoiceOption() {
            await this.axios.get('InvoiceUtilities/GetInternalInvoiceOption').then((response) => {
                this.internalInvoiceOptionItems = response.data;
            })
                .catch(err => console.log(err));
        },
        async getRelatedInvoiceOptions() {
            await this.axios.get('InvoiceUtilities/GetRelatedInvoiceOption').then((response) => {
                this.allRelatedInvoiceOptionsItems = response.data;
            })
                .catch(err => console.log(err));
        },
        async getRelatedInternalInvoiceOptions() {
            await this.axios.get('InvoiceUtilities/GetRelatedInternalInvoiceOption').then((response) => {
                this.allRelatedInternalInvoiceOptionsItems = response.data;
            })
                .catch(err => console.log(err));
        },
        periodTypeChanged() {
            this.vatPeriodItems = this.vatPeriodAllItems.filter(p => { return p.vatPeriodTypeId === this.vatRecord.periodType.vatPeriodTypeId });
            this.vatRecord.period = this.vatPeriodItems[0];
        },
        async vatRecordData() {
            if (this.$route.query.id != undefined) {
                await this.axios.get('VatRecord/GetIndividualVatRecordDetails?individualVatRecordId=' + this.$route.query.id).then((response) => {
                    this.vatRecord = response.data;
                    if(this.vatRecord.noRealEstateBaseAmount10)
                        this.vatRecord.noRealEstateBaseAmount10 = this.formatNumberToString(this.vatRecord.noRealEstateBaseAmount10);
                    if(this.vatRecord.noRealEstateBaseAmount20)
                        this.vatRecord.noRealEstateBaseAmount20 = this.formatNumberToString(this.vatRecord.noRealEstateBaseAmount20);
                    if(this.vatRecord.noRealEstateCalculatedVat20)
                        this.vatRecord.noRealEstateCalculatedVat20 = this.formatNumberToString(this.vatRecord.noRealEstateCalculatedVat20);
                    if(this.vatRecord.noRealEstateCalculatedVat10)
                        this.vatRecord.noRealEstateCalculatedVat10 = this.formatNumberToString(this.vatRecord.noRealEstateCalculatedVat10);

                    if(this.vatRecord.realEstateBaseAmount10)
                        this.vatRecord.realEstateBaseAmount10 = this.formatNumberToString(this.vatRecord.realEstateBaseAmount10);
                    if(this.vatRecord.realEstateBaseAmount20)
                        this.vatRecord.realEstateBaseAmount20 = this.formatNumberToString(this.vatRecord.realEstateBaseAmount20);
                    if(this.vatRecord.realEstateCalculatedVat20)
                        this.vatRecord.realEstateCalculatedVat20 = this.formatNumberToString(this.vatRecord.realEstateCalculatedVat20);
                    if(this.vatRecord.realEstateCalculatedVat10)
                        this.vatRecord.realEstateCalculatedVat10 = this.formatNumberToString(this.vatRecord.realEstateCalculatedVat10);

                    if(this.vatRecord.noChargeNoRealEstateBaseAmount10)
                        this.vatRecord.noChargeNoRealEstateBaseAmount10 = this.formatNumberToString(this.vatRecord.noChargeNoRealEstateBaseAmount10);
                    if(this.vatRecord.noChargeNoRealEstateBaseAmount20)
                        this.vatRecord.noChargeNoRealEstateBaseAmount20 = this.formatNumberToString(this.vatRecord.noChargeNoRealEstateBaseAmount20);
                    if(this.vatRecord.noChargeNoRealEstateCalculatedVat20)
                        this.vatRecord.noChargeNoRealEstateCalculatedVat20 = this.formatNumberToString(this.vatRecord.noChargeNoRealEstateCalculatedVat20);
                    if(this.vatRecord.noChargeNoRealEstateCalculatedVat10)
                        this.vatRecord.noChargeNoRealEstateCalculatedVat10 = this.formatNumberToString(this.vatRecord.noChargeNoRealEstateCalculatedVat10);

                    if(this.vatRecord.noChargeRealEstateBaseAmount10)
                        this.vatRecord.noChargeRealEstateBaseAmount10 = this.formatNumberToString(this.vatRecord.noChargeRealEstateBaseAmount10);
                    if(this.vatRecord.noChargeRealEstateBaseAmount20)
                        this.vatRecord.noChargeRealEstateBaseAmount20 = this.formatNumberToString(this.vatRecord.noChargeRealEstateBaseAmount20);
                    if(this.vatRecord.noChargeRealEstateCalculatedVat20)
                        this.vatRecord.noChargeRealEstateCalculatedVat20 = this.formatNumberToString(this.vatRecord.noChargeRealEstateCalculatedVat20);
                    if(this.vatRecord.noChargeRealEstateCalculatedVat10)
                        this.vatRecord.noChargeRealEstateCalculatedVat10 = this.formatNumberToString(this.vatRecord.noChargeRealEstateCalculatedVat10);
                    
                    this.vatRecord.vatAmount = this.formatNumberToString(this.vatRecord.vatAmount);
                    this.vatRecord.totalAmount = this.formatNumberToString(this.vatRecord.totalAmount);
                    this.initialVatRecord = JSON.parse(JSON.stringify(this.vatRecord));
                    if (this.vatRecord.status.vatRecordStatusId == 1) {
                        this.initialVatRecord.turnoverDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10);
                        this.vatRecord.turnoverDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10);
                    }
                    this.documentDirectionChanged();

                    this.vatPeriodItems = this.vatPeriodAllItems.filter(p => { return p.vatPeriodTypeId === this.vatRecord.periodType.vatPeriodTypeId });

                    if (this.vatRecord.documentType)
                        this.relatedInvoiceOptionsItems = this.allRelatedInvoiceOptionsItems.filter(item => { return item.vatRecordDocumentTypeId === this.vatRecord.documentType.vatRecordDocumentTypeId });
                    if (this.vatRecord.internalInvoiceOption)
                        this.relatedInternalInvoiceOptionsItems = this.allRelatedInternalInvoiceOptionsItems.filter(item => { return item.internalInvoiceOptionId === this.vatRecord.internalInvoiceOption.internalInvoiceOptionId });

                    console.log('vatrecord:')
                    console.log(this.vatRecord)
                    console.log('initial')
                    console.log(this.initialVatRecord)
                })
                    .catch(err => console.log(err));
            }
        },
        save() {
            this.executingOperation = true;
            if (this.$refs.vatRecordForm.validate()) {
                this.vatRecord.turnoverAmount = this.vatRecord.totalAmount;
                var formData = new FormData();
                formData.append('recordJson', JSON.stringify(this.vatRecord));
                this.axios.post('VatRecord/SaveIndividualVatRecord?organizationId=' + this.selectedOrganization, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }).then(response => {
                    if (response.data.message != null && response.data.individualVatRecordId <= 0) {
                        this.$root.displayNotificationMsg(response.data.message, 'warning')
                        this.executingOperation = false;
                        return
                    }
                    else if (response && response.status === 200) {
                        this.vatRecord = response.data;
                        this.initialVatRecord = JSON.parse(JSON.stringify(this.vatRecord));
                        this.$root.displayNotificationMsg(this.$t('individualVatRecordSavedMessage', { number: this.vatRecord.calculationNumber }), 'success');

                        this.$router.push({ name: 'IndividualVatRecord' });
                    }
                })
                    .catch(function (error) {
                        console.log(error)
                        if (error.response) {
                            console.log(error.response.data);
                            console.log(error.response.status);
                            console.log(error.response.headers);
                        }
                        else if (error.request) {
                            console.log(error.request);
                        }
                    })
            }
            else {
                this.$root.displayNotificationMsg(this.$t('dataRequired'), 'warning');
                this.executingOperation = false;
            }
        },
        async cancelChanges() {
            if (this.hasChanged && await this.$confirm(this.$t('cancelChangesMessage'), { title: this.$t('warning'), buttonTrueText: this.$t('yes'), buttonFalseText: this.$t('no'), persistent: true }))
                this.vatRecord = JSON.parse(JSON.stringify(this.initialVatRecord))
        },
        invoicePeriodStartRule() {
            const rules = []
            if (this.vatRecord.relatedInvoiceIssueDateTo && this.vatRecord.relatedInvoiceIssueDateTo.length > 0) {
                const rule = v => !!v || this.$t('startDateRequired')
                rules.push(rule)
            }

            if (this.vatRecord.relatedInvoiceIssueDateTo && this.vatRecord.relatedInvoiceIssueDateTo.length > 0) {
                const rule = v => (!!v && moment(this.vatRecord.relatedInvoiceIssueDateFrom).isBefore(this.vatRecord.relatedInvoiceIssueDateTo, 'day')) || this.$t('dateRule')
                rules.push(rule)
            }
            return rules
        },
        invoicePeriodEndRule() {
            const rules = []
            if (this.vatRecord.relatedInvoiceIssueDateFrom && this.vatRecord.relatedInvoiceIssueDateFrom.length > 0) {
                const rule = v => !!v || this.$t('endDateRequired')
                rules.push(rule)
            }
            return rules
        },
        documentNumberRule() {
            const rules = [v => !!v || this.$t('calculationNumberRequired')]
            if (this.foundNumber) {
                const rule = this.$t('invoiceNumberExist')
                rules.push(rule)
            }
            return rules
        },
        checkDocumentNumber() {
            if (this.vatRecord.calculationNumber.length > 1)
                this.axios.get('VatRecord/CheckIndividualVatNumber?organizationId=' + this.$store.getters.organizationId + '&id=' + this.vatRecord.individualVatRecordId + '&number=' + this.vatRecord.calculationNumber).then((response) => {
                    this.foundNumber = response.data
                })
                    .catch(err => console.log(err))
        },
        send() {
            this.executingOperation = true;

            if (this.$refs.vatRecordForm.validate()) {
                this.vatRecord.turnoverAmount = this.vatRecord.totalAmount;
                var formData = new FormData();
                formData.append('recordJson', JSON.stringify(this.vatRecord));
                this.axios.post('VatRecord/SendIndividualVatRecordWithFormattedValues?organizationId=' + this.selectedOrganization + '&hasChanged=' + this.hasChanged, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }).then(response => {
                    if (response.data.message != null && response.data.individualVatId <= 0) {
                        this.$root.displayNotificationMsg(response.data.message, 'warning')
                        this.executingOperation = false;
                        return
                    }
                    else if (response && response.status === 200) {
                        this.vatRecord = response.data;
                        this.initialVatRecord = JSON.parse(JSON.stringify(this.vatRecord));
                        this.$root.displayNotificationMsg(this.$t('individualVatRecordSendMessage', { number: this.vatRecord.calculationNumber }), 'success');

                        this.$router.push({ name: 'IndividualVatRecord' });
                    }
                })
                    .catch(function (error) {
                        console.log(error)
                        if (error.response) {
                            console.log(error.response.data);
                            console.log(error.response.status);
                            console.log(error.response.headers);
                        }
                        else if (error.request) {
                            console.log(error.request);
                        }
                    })
            }
            else {
                this.$root.displayNotificationMsg(this.$t('dataRequired'), 'warning')
                this.executingOperation = false;
            }
        },
        correct() {
            this.correction = true;
            this.vatRecord.turnoverDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10);
        },
        cancelCorrection() {
            this.correction = false;

            this.vatRecord.turnoverDate = this.initialVatRecord.turnoverDate;
        },
        saveCorrection() {
            this.executingOperation = true;
            this.vatRecord.individualVatRecordId = -1;
            this.vatRecord.turnoverAmount = this.vatRecord.totalAmount;
            var formData = new FormData();
            formData.append('recordJson', JSON.stringify(this.vatRecord));
            this.axios.post('VatRecord/CorrectIndividualVatRecordWithFormattedValues?organizationId=' + this.selectedOrganization + '&hasChanged=' + this.hasChanged, formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            }).then(response => {
                if (response.data.message != null && response.data.individualVatId <= 0) {
                    this.$root.displayNotificationMsg(response.data.message, 'warning')
                    this.executingOperation = false;
                    return
                }
                else if (response && response.status === 200) {
                    this.correction = false;
                    this.vatRecord = response.data;
                    this.initialVatRecord = JSON.parse(JSON.stringify(this.vatRecord));
                    this.$root.displayNotificationMsg(this.$t('individualVatRecordCorrectionMessage', { number: this.vatRecord.calculationNumber }), 'success');

                    this.$router.push({ name: 'IndividualVatRecord' });
                }
            })
                .catch(function (error) {
                    console.log(error)
                    if (error.response) {
                        console.log(error.response.data);
                        console.log(error.response.status);
                        console.log(error.response.headers);
                    }
                    else if (error.request) {
                        console.log(error.request);
                    }
                })

        },
        async annul() {
            this.executingOperation = true;
            if (await this.$confirm(this.$t('cancelVatRecordMessage', { number: this.vatRecord.calculationNumber }), { title: this.$t('warning'), buttonTrueText: this.$t('yes'), buttonFalseText: this.$t('no'), persistent: true })) {
                this.axios.post('VatRecord/CancelIndividualVatRecord?organizationId=' + this.selectedOrganization + '&id=' + this.vatRecord.individualVatId).then((response) => {
                    if (response && response.status === 200) {
                        this.$root.displayNotificationMsg(this.$t('individualVatRecordCancelMessage', { number: this.vatRecord.calculationNumber }), 'success');
                        this.$router.push({ name: 'IndividualVatRecord' });
                    }
                })
                    .catch(function (error) {
                        console.log(error)
                        if (error.response) {
                            console.log(error.response.data);
                            console.log(error.response.status);
                            console.log(error.response.headers);
                        }
                        else if (error.request) {
                            console.log(error.request);
                        }
                    })
            }
        },
        async deleteRecord() {
            this.executingOperation = true;
            if (await this.$confirm(this.$t('cancelVatRecordMessage', { number: this.vatRecord.calculationNumber }), { title: this.$t('warning'), buttonTrueText: this.$t('yes'), buttonFalseText: this.$t('no'), persistent: true })) {
                this.axios.delete('VatRecord/deleteIndividualVatRecord/' + this.vatRecord.individualVatRecordId).then((response) => {
                    if (response && response.status === 200) {
                        this.$root.displayNotificationMsg(this.$t('individualVatRecordDeleteMessage', { number: this.vatRecord.calculationNumber }), 'success');
                        this.$router.push({ name: 'IndividualVatRecord' });
                    }
                })
                    .catch((error) => {
                        console.log(error);
                        this.$root.displayNotificationMsg(error.response.data, 'warning');
                        this.executingOperation = false;
                    })
            }
            else {
                this.executingOperation = false;
            }
        },
        addRelatedDocument() {
            if (this.relatedDocument && this.relatedDocument.length > 0) {
                const found = this.vatRecord.relatedDocuments.some(el => el.documentNumber === this.relatedDocument);
                if (!found) {
                    let rd = {
                        documentNumber: this.relatedDocument
                    }
                    this.vatRecord.relatedDocuments.push(rd)
                    this.relatedDocument = null
                }
            }
        },
        calculateTotal() {
            let vat = 0;
            let base = 0;
            if (this.vatRecord.noRealEstateCalculatedVat20)
                vat = vat + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noRealEstateCalculatedVat20));
            if (this.vatRecord.noRealEstateCalculatedVat10)
                vat = vat + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noRealEstateCalculatedVat10));
            if (this.vatRecord.noRealEstateBaseAmount20)
                base = base + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noRealEstateBaseAmount20));
            if (this.vatRecord.noRealEstateBaseAmount10)
                base = base + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noRealEstateBaseAmount10));

            if (this.vatRecord.realEstateCalculatedVat20)
                vat = vat + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.realEstateCalculatedVat20));
            if (this.vatRecord.realEstateCalculatedVat10)
                vat = vat + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.realEstateCalculatedVat10));
            if (this.vatRecord.realEstateBaseAmount20)
                base = base + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.realEstateBaseAmount20));
            if (this.vatRecord.realEstateBaseAmount10)
                base = base + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.realEstateBaseAmount10));

            if (this.vatRecord.noChargeNoRealEstateCalculatedVat20)
                vat = vat + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noChargeNoRealEstateCalculatedVat20));
            if (this.vatRecord.noChargeNoRealEstateCalculatedVat10)
                vat = vat + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noChargeNoRealEstateCalculatedVat10));
            if (this.vatRecord.noChargeNoRealEstateBaseAmount20)
                base = base + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noChargeNoRealEstateBaseAmount20));
            if (this.vatRecord.noChargeNoRealEstateBaseAmount10)
                base = base + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noChargeNoRealEstateBaseAmount10));

            if (this.vatRecord.noChargeRealEstateCalculatedVat20)
                vat = vat + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noChargeRealEstateCalculatedVat20));
            if (this.vatRecord.noChargeRealEstateCalculatedVat10)
                vat = vat + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noChargeRealEstateCalculatedVat10));
            if (this.vatRecord.noChargeRealEstateBaseAmount20)
                base = base + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noChargeRealEstateBaseAmount20));
            if (this.vatRecord.noChargeRealEstateBaseAmount10)
                base = base + parseFloat(this.convertFormattedStringToNumber(this.vatRecord.noChargeRealEstateBaseAmount10));

            this.vatRecord.totalAmount = this.formatNumberToString(base + vat);
            this.vatRecord.vatAmount = this.formatNumberToString(vat);
        },
        removeFromRelatedDocument(index) {
            this.vatRecord.relatedDocuments.splice(index, 1)
        },
        clearPaymentDate() {
            this.vatRecord.paymentDate = '';
        },
        clearTurnoverDate() {
            this.vatRecord.turnoverDate = '';
        },
        formatNumberToString(value) {
            value = value.toString().replace(",", ".");
            var formatter = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return formatter.format(value);
        },
        convertFormattedStringToNumber(value) {
            while (value.includes(".")) {

                value = value.replace(".", "")
            }
            value = value.replace(",", ".")

            return value
        },
        format() {
            if (this.vatRecord.noRealEstateBaseAmount20 && this.vatRecord.noRealEstateBaseAmount20.length > 0)
                this.vatRecord.noRealEstateBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noRealEstateBaseAmount20));
            if (this.vatRecord.noRealEstateBaseAmount10 && this.vatRecord.noRealEstateBaseAmount10.length > 0)
                this.vatRecord.noRealEstateBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noRealEstateBaseAmount10));
            if (this.vatRecord.noRealEstateCalculatedVat20 && this.vatRecord.noRealEstateCalculatedVat20.length > 0)
                this.vatRecord.noRealEstateCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noRealEstateCalculatedVat20));
            if (this.vatRecord.noRealEstateCalculatedVat10 && this.vatRecord.noRealEstateCalculatedVat10.length > 0)
                this.vatRecord.noRealEstateCalculatedVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noRealEstateCalculatedVat10));

            if (this.vatRecord.realEstateBaseAmount20 && this.vatRecord.realEstateBaseAmount20.length > 0)
                this.vatRecord.realEstateBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.realEstateBaseAmount20));
            if (this.vatRecord.realEstateBaseAmount10 && this.vatRecord.realEstateBaseAmount10.length > 0)
                this.vatRecord.realEstateBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.realEstateBaseAmount10));
            if (this.vatRecord.realEstateCalculatedVat20 && this.vatRecord.realEstateCalculatedVat20.length > 0)
                this.vatRecord.realEstateCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.realEstateCalculatedVat20));
            if (this.vatRecord.realEstateCalculatedVat10 && this.vatRecord.realEstateCalculatedVat10.length > 0)
                this.vatRecord.realEstateCalculatedVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.realEstateCalculatedVat10));

            if (this.vatRecord.noChargeNoRealEstateBaseAmount20 && this.vatRecord.noChargeNoRealEstateBaseAmount20.length > 0)
                this.vatRecord.noChargeNoRealEstateBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeNoRealEstateBaseAmount20));
            if (this.vatRecord.noChargeNoRealEstateBaseAmount10 && this.vatRecord.noChargeNoRealEstateBaseAmount10.length > 0)
                this.vatRecord.noChargeNoRealEstateBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeNoRealEstateBaseAmount10));
            if (this.vatRecord.noChargeNoRealEstateCalculatedVat20 && this.vatRecord.noChargeNoRealEstateCalculatedVat20.length > 0)
                this.vatRecord.noChargeNoRealEstateCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeNoRealEstateCalculatedVat20));
            if (this.vatRecord.noChargeNoRealEstateCalculatedVat10 && this.vatRecord.noChargeNoRealEstateCalculatedVat10.length > 0)
                this.vatRecord.noChargeNoRealEstateCalculatedVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeNoRealEstateCalculatedVat10));

            if (this.vatRecord.noChargeRealEstateBaseAmount20 && this.vatRecord.noChargeRealEstateBaseAmount20.length > 0)
                this.vatRecord.noChargeRealEstateBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeRealEstateBaseAmount20));
            if (this.vatRecord.noChargeRealEstateBaseAmount10 && this.vatRecord.noChargeRealEstateBaseAmount10.length > 0)
                this.vatRecord.noChargeRealEstateBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeRealEstateBaseAmount10));
            if (this.vatRecord.noChargeRealEstateCalculatedVat20 && this.vatRecord.noChargeRealEstateCalculatedVat20.length > 0)
                this.vatRecord.noChargeRealEstateCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeRealEstateCalculatedVat20));
            if (this.vatRecord.noChargeRealEstateCalculatedVat10 && this.vatRecord.noChargeRealEstateCalculatedVat10.length > 0)
                this.vatRecord.noChargeRealEstateCalculatedVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeRealEstateCalculatedVat10));

            if (this.vatRecord.vatAmount && this.vatRecord.vatAmount.length > 0)
                this.vatRecord.vatAmount = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.vatAmount));
            if (this.vatRecord.totalAmount && this.vatRecord.totalAmount.length > 0)
                this.vatRecord.totalAmount = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.totalAmount));
        },
        documentTypeChanged() {
            this.vatRecord.relatedDocuments = [];
            if (this.vatRecord.documentType && this.vatRecord.documentType.vatRecordDocumentTypeId > 4) {
                this.vatRecord.internalInvoiceOption = { internalInvoiceOptionId: 1};
            }
            else {
                this.vatRecord.internalInvoiceOption= { internalInvoiceOptionId: -1};
            }

            if (this.vatRecord.documentType)
                this.relatedInvoiceOptionsItems = this.allRelatedInvoiceOptionsItems.filter(item => { return item.vatRecordDocumentTypeId === this.vatRecord.documentType.vatRecordDocumentTypeId });

            if (this.vatRecord.internalInvoiceOption)
                this.relatedInternalInvoiceOptionsItems = this.allRelatedInternalInvoiceOptionsItems.filter(item => { return item.internalInvoiceOptionId === this.vatRecord.internalInvoiceOption.internalInvoiceOptionId });

            this.vatRecord.relatedInvoiceOption = { relatedInvoiceOptionId: 0 }
            this.vatRecord.relatedDocuments = [];
            this.vatRecord.relatedInvoiceIssueDateFrom = "";
            this.vatRecord.relatedInvoiceIssueDateTo = "";
            console.log(this.vatRecord);
        },
        internalInvoiceOptionChanged() {
            if (this.vatRecord.documentType.vatRecordDocumentTypeId > 4)
                this.relatedInternalInvoiceOptionsItems = this.allRelatedInternalInvoiceOptionsItems.filter(item => { return item.internalInvoiceOptionId === this.vatRecord.internalInvoiceOption.internalInvoiceOptionId });
            this.vatRecord.relatedInternalInvoiceOption = { relatedInternalInvoiceOptionId: 0 }
        },
        relatedInvoiceOptionChanged() {
            this.vatRecord.relatedDocuments = [];
            this.vatRecord.relatedInvoiceIssueDateFrom = "";
            this.vatRecord.relatedInvoiceIssueDateTo = "";
        },
        relatedInternalInvoiceOptionChanged() {
            this.vatRecord.relatedDocuments = [];
            this.vatRecord.relatedInvoiceIssueDateFrom = "";
            this.vatRecord.relatedInvoiceIssueDateTo = "";
        },
        documentDirectionChanged() {
            if (this.vatRecord.documentDirection.documentDirectionId == 1) {
                this.documentTypesItems = this.allDocumentTypesItems.filter(item => { return item.vatRecordDocumentTypeId > 4 });
                this.dynamicCol = 4;
                this.mdDynamic = 2;
            }

            else {
                this.documentTypesItems = this.allDocumentTypesItems.filter(item => { return item.vatRecordDocumentTypeId <= 4 });
                this.dynamicCol = 8;
                this.mdDynamic = 4;
            }
            console.log(this.dynamicCol);
            this.vatRecord.documentType = this.documentTypesItems[0];
            this.documentTypeChanged();
        },
        getDocumentTypeText(item) {
            if (i18n.locale === 'eng')
                return item.documentTypeEng;
            else
                return item.documentTypeSrb;
        },
        getRelatedInvoiceOptionText(item) {
            if (i18n.locale === 'eng')
                return item.optionEng;
            else
                return item.option;
        },
        getRelatedInternalInvoiceOptionText(item) {
            if (i18n.locale === 'eng')
                return item.optionEng;
            else
                return item.optionSrb;
        },
        getDocumentDirectionText(item) {
            if (i18n.locale === 'eng')
                return item.directionEng;
            else
                return item.direction;
        },
        getVatPeriodTypeText(item) {
            if (i18n.locale === 'eng')
                return item.typeEng;
            else
                return item.type;
        },
        getVatPeriodText(item) {
            if (i18n.locale === 'eng')
                return item.periodEng;
            else
                return item.periodSrb;
        },
        getOptionText(item) {
            if (i18n.locale === 'eng')
                return item.optionEng;
            else
                return item.optionSrb;
        },
        documentStatusText() {
            if (localStorage.getItem('language') === 'eng')
                return this.vatRecord.status.vatRecordStatusEng;
            else
                return this.vatRecord.status.vatRecordStatus;
        },
        beforeWindowUnload(e) {
            e.preventDefault()
            e.returnValue = ''
        },
    }
}

</script>
